import React from 'react';

import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  DEFAULT_CURRENCY,
  formatDealOptionCurrency,
  formatDealOptionPercent,
} from '../dealOptions/dealOptionsFormatters';

import { getGuestSlot, hasArchiveDisagreement, isArchivedRow } from './egsUtils';

const DETAIL_TABLE_COLUMN_COUNT = 9;

interface Props {
  rows: Array<OpportunityRelatedExtraGuestSurcharge>;
}

export default function EgsGuestTypeTable({ rows }: Props) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Guest type</TableCell>
          <TableCell>Age range</TableCell>
          <TableCell>Collected by</TableCell>
          <TableCell>Bedding</TableCell>
          <TableCell>Inclusions</TableCell>
          <TableCell align="right">Cost</TableCell>
          <TableCell align="right">Converted cost</TableCell>
          <TableCell align="right">Sell price</TableCell>
          <TableCell align="right">Margin</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={DETAIL_TABLE_COLUMN_COUNT}>
              <Typography color="text.secondary" variant="body2">
                No surcharges for this room.
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {rows.map((surcharge) => {
          const slot = getGuestSlot(surcharge.extra_guest_type__c);

          return (
            <TableRow key={surcharge.id}>
              <TableCell>
                <Stack alignItems="center" direction="row" spacing={1}>
                  <Chip
                    color={slot ? 'default' : 'warning'}
                    label={surcharge.extra_guest_type__c || 'Unknown'}
                    size="small"
                    variant="outlined"
                  />
                  {isArchivedRow(surcharge) && <Chip color="default" label="Archived" size="small" />}
                  {hasArchiveDisagreement(surcharge) && (
                    <Chip color="warning" label="Archive unclear" size="small" variant="outlined" />
                  )}
                </Stack>
              </TableCell>
              <TableCell>{surcharge.age_range__c || '—'}</TableCell>
              <TableCell>{surcharge.collected_by__c || '—'}</TableCell>
              <TableCell>{surcharge.other_extra_bedding_type__c || surcharge.bedding_type__c || '—'}</TableCell>
              <TableCell>{surcharge.inclusions__c || '—'}</TableCell>
              <TableCell align="right">
                {formatDealOptionCurrency(surcharge.cost_price_currency, surcharge.cost_price__c)}
              </TableCell>
              <TableCell align="right">
                {formatDealOptionCurrency(DEFAULT_CURRENCY, surcharge.converted_cost__c)}
              </TableCell>
              <TableCell align="right">
                {formatDealOptionCurrency(DEFAULT_CURRENCY, surcharge.sell_price_aud__c)}
              </TableCell>
              <TableCell align="right">{formatDealOptionPercent(surcharge.margin__c)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
